/**
 * EVENTSFLO-ADMIN-BACKUP-OFFSITE-V1
 *
 * Read-only summary card + manual-copy dialog for the SuperAdmin
 * offsite-backup feature. UI is purely additive — V1 supports ONLY
 * manual copy of an existing archive to the server-configured
 * offsite directory. NO restore-from-offsite, NO scheduler, NO
 * cron, NO email controls.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudUpload, ShieldCheck, ShieldOff } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  getOffsiteSettings,
  offsitePreflight,
  offsiteCopy,
  listBackups,
} from "./backupApi";
import { formatBytes, formatDate, PAGE_SIZE } from "./backupFormatters";

const _STATUS_COLOR = {
  ok: "bg-green-500/20 text-green-400 border-green-500/30",
  failed: "bg-red-500/20 text-red-400 border-red-500/30",
};

export default function BackupOffsiteCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getOffsiteSettings();
      setData(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load offsite settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  if (loading || !data) {
    return (
      <Card data-testid="offsite-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CloudUpload className="w-4 h-4" /> Offsite backup
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-zinc-400">Loading…</CardContent>
      </Card>
    );
  }

  const isSafe = data.configured && data.safe && data.writable;

  return (
    <>
      <Card data-testid="offsite-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <CloudUpload className="w-4 h-4" /> Offsite backup
          </CardTitle>
          <Button
            size="sm"
            onClick={() => setCopyOpen(true)}
            disabled={!isSafe}
            data-testid="offsite-copy-btn"
          >
            <CloudUpload className="w-3 h-3 mr-1" /> Copy a backup offsite
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-zinc-400">Configured</div>
              <div className="font-medium flex items-center gap-1">
                {isSafe ? (
                  <Badge
                    className="bg-green-500/20 text-green-400 border-green-500/30"
                    data-testid="offsite-configured-badge"
                  >
                    <ShieldCheck className="w-3 h-3 mr-1" /> Yes
                  </Badge>
                ) : (
                  <Badge
                    className="bg-amber-500/20 text-amber-400 border-amber-500/30"
                    data-testid="offsite-configured-badge"
                  >
                    <ShieldOff className="w-3 h-3 mr-1" />
                    {data.configured ? "Misconfigured" : "Not configured"}
                  </Badge>
                )}
              </div>
            </div>
            <div>
              <div className="text-zinc-400">Target</div>
              <div className="font-medium text-xs">
                {data.target_label || "—"}
              </div>
            </div>
            <div className="col-span-2" data-testid="offsite-last-copy">
              <div className="text-zinc-400">Last copy</div>
              {data.last_copy_summary ? (
                <div className="text-xs space-y-0.5">
                  <Badge
                    className={
                      _STATUS_COLOR[data.last_copy_summary.status] ||
                      "bg-zinc-500/20 text-zinc-300 border-zinc-500/30"
                    }
                  >
                    {data.last_copy_summary.status}
                  </Badge>{" "}
                  <span className="font-mono">
                    {data.last_copy_summary.backup_id?.slice(0, 8)}…
                  </span>
                  <div className="text-zinc-500">
                    {formatDate(data.last_copy_summary.started_at)} →{" "}
                    {formatDate(data.last_copy_summary.completed_at)} (
                    {formatBytes(data.last_copy_summary.bytes_copied)})
                  </div>
                </div>
              ) : (
                <div className="text-xs text-zinc-500">No copies yet.</div>
              )}
            </div>
            {data.config_error ? (
              <div className="col-span-2 md:col-span-4 text-xs text-amber-300">
                Config error: <span className="font-mono">{data.config_error}</span>
              </div>
            ) : null}
          </div>
          <div className="mt-3 text-xs text-zinc-500">
            V1 supports manual offsite copy only. Scheduled offsite sync,
            restore-from-offsite, destructive offsite delete and email
            notifications are not implemented in this build.
          </div>
        </CardContent>
      </Card>

      <OffsiteCopyDialog
        open={copyOpen}
        onOpenChange={setCopyOpen}
        targetLabel={data.target_label}
        onCopied={fetchSettings}
      />
    </>
  );
}

function OffsiteCopyDialog({ open, onOpenChange, targetLabel, onCopied }) {
  const [backups, setBackups] = useState([]);
  const [backupId, setBackupId] = useState("");
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState("");
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setBackupId("");
    setPreflight(null);
    setTypedConfirm("");
    setResult(null);
    listBackups({ limit: PAGE_SIZE, offset: 0, include_expired: true })
      .then(({ data }) => {
        setBackups((data.items || []).filter((b) => b.exists_on_disk));
      })
      .catch((e) => {
        console.error(e);
        toast.error("Failed to load archives");
      });
  }, [open]);

  const runPreflight = async () => {
    if (!backupId) {
      toast.error("Select a backup first");
      return;
    }
    setPreflightLoading(true);
    try {
      const { data } = await offsitePreflight({ backup_id: backupId });
      setPreflight(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Preflight failed";
      toast.error(typeof msg === "object" ? JSON.stringify(msg) : msg);
    } finally {
      setPreflightLoading(false);
    }
  };

  const runCopy = async () => {
    if (typedConfirm !== "COPY BACKUP OFFSITE") {
      toast.error("Type COPY BACKUP OFFSITE to confirm");
      return;
    }
    setCopying(true);
    try {
      const { data } = await offsiteCopy({
        backup_id: backupId,
        confirm_phrase: "COPY BACKUP OFFSITE",
      });
      setResult(data);
      toast.success("Offsite copy complete");
      await onCopied?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Copy failed";
      toast.error(typeof msg === "object" ? JSON.stringify(msg) : msg);
    } finally {
      setCopying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="offsite-copy-dialog">
        <DialogHeader>
          <DialogTitle>Copy a backup offsite</DialogTitle>
          <DialogDescription>
            Copies an existing archive from the manual-backup directory to the
            configured offsite target ({targetLabel}). SHA256 is verified. The
            source archive is not modified. V1 does NOT restore from offsite.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-3 text-sm">
            <div className="space-y-1">
              <Label>Choose a backup</Label>
              <Select value={backupId} onValueChange={setBackupId}>
                <SelectTrigger data-testid="offsite-backup-select">
                  <SelectValue placeholder="Select a backup…" />
                </SelectTrigger>
                <SelectContent>
                  {backups.map((b) => (
                    <SelectItem key={b.backup_id} value={b.backup_id}>
                      <span className="font-mono">
                        {b.backup_id.slice(0, 8)}…
                      </span>{" "}
                      — {formatBytes(b.size_bytes)} — {formatDate(b.generated_at)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={runPreflight}
              disabled={!backupId || preflightLoading}
              data-testid="offsite-preflight-btn"
            >
              {preflightLoading ? "Running preflight…" : "Run preflight"}
            </Button>

            {preflight && (
              <div className="rounded border border-zinc-700 bg-zinc-900/40 p-3 text-xs space-y-1">
                <div>
                  <strong>Target:</strong> {preflight.target_label}
                </div>
                <div>
                  <strong>File:</strong> {preflight.archive_filename}
                </div>
                <div>
                  <strong>Size:</strong> {formatBytes(preflight.source_size_bytes)}
                </div>
                {preflight.warnings?.length ? (
                  <div className="text-amber-300">
                    Warnings: {preflight.warnings.join(", ")}
                  </div>
                ) : null}
              </div>
            )}

            {preflight && (
              <div className="space-y-1">
                <Label htmlFor="offsite-typed-confirm">
                  Type <span className="font-mono">COPY BACKUP OFFSITE</span> to enable Copy
                </Label>
                <Input
                  id="offsite-typed-confirm"
                  value={typedConfirm}
                  onChange={(e) => setTypedConfirm(e.target.value)}
                  placeholder="COPY BACKUP OFFSITE"
                  data-testid="offsite-typed-confirm-input"
                />
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="rounded border border-green-700 bg-green-900/20 p-3 text-xs space-y-1" data-testid="offsite-copy-result">
            <div className="font-semibold text-green-400">Copy complete</div>
            <div>
              <strong>Target:</strong> {result.target_label}
            </div>
            <div>
              <strong>Bytes copied:</strong> {formatBytes(result.bytes_copied)}
            </div>
            <div>
              <strong>SHA256 (first 16):</strong>{" "}
              <span className="font-mono">{(result.sha256 || "").slice(0, 16)}…</span>
            </div>
            <div>
              <strong>Copy ID:</strong>{" "}
              <span className="font-mono">{result.copy_id}</span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="offsite-cancel-btn"
          >
            Close
          </Button>
          {!result && (
            <Button
              onClick={runCopy}
              disabled={copying || !preflight || typedConfirm !== "COPY BACKUP OFFSITE"}
              data-testid="offsite-confirm-copy-btn"
            >
              {copying ? "Copying…" : "Copy offsite"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
